import { Affix, Alert, Button, Group, Paper, Stack, Text } from '@mantine/core'
import { isEmpty } from 'lodash-es'
import type { TFunction } from 'i18next'
import type { GapFix } from './gapFix'
import type { RemovalPlan } from './removalRange'
import type { EditState, PendingRemoval } from './types'

type PlannerStatusBannersProps = {
  t: TFunction<'routePlanner'>
  isDeleteMode: boolean
  isPathBuildMode: boolean
  pendingRemoval: PendingRemoval | null
  removalPlan: RemovalPlan | null
  onConfirmRemoval: () => void
  shortestPathHint: Array<{ street_segment_id: string }> | null
  editState: EditState | null
  addTarget: string | null
  setAddTarget: (id: string | null) => void
  onAcceptShortestPath: () => void
  onCommitEdit: () => void
  onCancelEdit: () => void
  routableNodeIds: Set<string>
  gapFixes: GapFix[]
  gapFixPreview: boolean
  setGapFixPreview: (preview: boolean) => void
  onApplyGapFixes: () => void
}

export function PlannerStatusBanners({
  t,
  isDeleteMode,
  isPathBuildMode,
  pendingRemoval,
  removalPlan,
  onConfirmRemoval,
  shortestPathHint,
  editState,
  addTarget,
  setAddTarget,
  onAcceptShortestPath,
  onCommitEdit,
  onCancelEdit,
  routableNodeIds,
  gapFixes,
  gapFixPreview,
  setGapFixPreview,
  onApplyGapFixes,
}: PlannerStatusBannersProps) {
  return (
    <>
      {isDeleteMode && pendingRemoval && (
        <Affix position={{ bottom: 24, left: '50%' }} style={{ transform: 'translateX(-50%)' }}>
          <Paper shadow="md" radius="md">
            <Alert color="red" variant="filled" title={t('removalPendingTitle')}>
              <Group>
                <Text size="sm">
                  {removalPlan?.status === 'ready'
                    ? removalPlan.mode === 'truncate'
                      ? t('removalPendingTruncateMessage', {
                          count: pendingRemoval.rangeEnd - pendingRemoval.rangeStart + 1,
                          from: pendingRemoval.rangeStart + 1,
                          to: pendingRemoval.rangeEnd + 1,
                        })
                      : removalPlan.mode === 'gap'
                        ? t('removalPendingGapMessage', {
                            count: pendingRemoval.rangeEnd - pendingRemoval.rangeStart + 1,
                            from: pendingRemoval.rangeStart + 1,
                            to: pendingRemoval.rangeEnd + 1,
                          })
                        : t('removalPendingReconnectMessage', {
                            count: pendingRemoval.rangeEnd - pendingRemoval.rangeStart + 1,
                            from: pendingRemoval.rangeStart + 1,
                            to: pendingRemoval.rangeEnd + 1,
                            segments: removalPlan.replacement.length,
                          })
                    : t('removalInvalidMessage')}
                </Text>
                <Button
                  size="xs"
                  variant="white"
                  color="red"
                  onClick={onConfirmRemoval}
                  disabled={removalPlan?.status !== 'ready'}
                >
                  {t('confirmRemoval')}
                </Button>
              </Group>
            </Alert>
          </Paper>
        </Affix>
      )}

      {isPathBuildMode && shortestPathHint && !editState && (
        <Affix position={{ bottom: 24, left: '50%' }} style={{ transform: 'translateX(-50%)' }}>
          <Paper shadow="md" radius="md">
            <Alert color="yellow" variant="filled" title={t('shortestPathReadyTitle')}>
              <Group>
                <Text size="sm">{t('shortestPathReadyMessage')}</Text>
                <Button size="xs" variant="white" color="yellow" onClick={onAcceptShortestPath}>
                  {t('acceptShortestPath', { segments: shortestPathHint.length })}
                </Button>
                {addTarget && (
                  <Button size="xs" variant="white" onClick={() => setAddTarget(null)}>
                    {t('clearTarget')}
                  </Button>
                )}
              </Group>
            </Alert>
          </Paper>
        </Affix>
      )}

      {isPathBuildMode && addTarget && !shortestPathHint && !editState && (
        <Affix position={{ bottom: 24, left: '50%' }} style={{ transform: 'translateX(-50%)' }}>
          <Paper shadow="md" radius="md">
            <Alert color="gray" variant="filled" title={t('noPathFoundTitle')}>
              <Group>
                <Text size="sm">
                  {!routableNodeIds.has(addTarget) ? t('noPathFoundNonRoutableMessage') : t('noPathFoundMessage')}
                </Text>
                <Button size="xs" variant="white" onClick={() => setAddTarget(null)}>
                  {t('clearTarget')}
                </Button>
              </Group>
            </Alert>
          </Paper>
        </Affix>
      )}

      {isPathBuildMode && editState && (
        <Affix position={{ bottom: 24, left: '50%' }} style={{ transform: 'translateX(-50%)' }}>
          <Paper shadow="md" radius="md">
            <Alert color="blue" variant="filled" title={t('editModeTitle')}>
              <Stack gap="xs">
                <Text size="sm">
                  {addTarget && !shortestPathHint
                    ? !routableNodeIds.has(addTarget)
                      ? t('noPathFoundNonRoutableMessage')
                      : t('noPathFoundMessage')
                    : t('editModeMessage')}
                </Text>
                <Group>
                  {shortestPathHint && (
                    <Button size="xs" variant="white" color="violet" onClick={onAcceptShortestPath}>
                      {t('acceptShortestPath', { segments: shortestPathHint.length })}
                    </Button>
                  )}
                  {addTarget && (
                    <Button size="xs" variant="white" onClick={() => setAddTarget(null)}>
                      {t('clearTarget')}
                    </Button>
                  )}
                  <Button size="xs" variant="white" color="green" onClick={onCommitEdit}>
                    {t('commitEdit')}
                  </Button>
                  <Button size="xs" variant="white" color="red" onClick={onCancelEdit}>
                    {t('cancelEdit')}
                  </Button>
                </Group>
              </Stack>
            </Alert>
          </Paper>
        </Affix>
      )}

      {!isEmpty(gapFixes) && (
        <Affix position={{ top: 16, left: 16 }}>
          <Paper shadow="md" radius="md" maw={360} w="fit-content">
            <Alert color="orange" variant="filled" title={t('gapWarningTitle', { count: gapFixes.length })}>
              <Stack gap="xs">
                <Text size="sm">
                  {gapFixPreview ? t('gapPreviewMessage') : t('gapWarningMessage', { count: gapFixes.length })}
                </Text>
                <Group>
                  {gapFixPreview ? (
                    <>
                      <Button size="xs" variant="white" color="orange" onClick={onApplyGapFixes}>
                        {t('applyGapFixes')}
                      </Button>
                      <Button size="xs" variant="white" onClick={() => setGapFixPreview(false)}>
                        {t('hideGapPreview')}
                      </Button>
                    </>
                  ) : (
                    <Button size="xs" variant="white" color="orange" onClick={() => setGapFixPreview(true)}>
                      {t('showGapFix', { count: gapFixes.length })}
                    </Button>
                  )}
                </Group>
              </Stack>
            </Alert>
          </Paper>
        </Affix>
      )}
    </>
  )
}
